import { ZZFX_SAMPLE_RATE as SR } from '../lib/zzfx';

/**
 * The hurt punch (ADR-0118): a saturated cloth-and-body punch, past what ZzFX can make (its noise is a pitched wave):
 * a pitch-dropping sine thump, low-passed white noise and a 400 Hz band of noise, summed and driven through tanh.
 * `size` scales every decay (1 = the full 158 ms punch); a series of hits plays it smaller and smaller.
 * Seeded noise, so it renders the same every time; levelled to the ZzFX hurt it replaced.
 */
export function punchSamples(size: number): Float32Array {
  const thump = env(sweep(0.175 * size, 220, 70, (0.035 * size) / 3), 0.0008, 0.0245 * size);
  const slap = env(hp(lp(noise(0.12 * size, 7), 3500), 200), 0.0005, 0.008 * size);
  const body = env(bp(noise(0.1 * size, 8), 400, 1), 0.0005, 0.02 * size);
  const out = new Float32Array(Math.max(thump.length, slap.length, body.length));
  for (let i = 0; i < out.length; i++) {
    const v = 0.45 * (thump[i] ?? 0) + 1.6 * (slap[i] ?? 0) + 0.96 * (body[i] ?? 0);
    out[i] = (Math.tanh(DRIVE * v) / Math.tanh(DRIVE)) * GAIN;
  }
  // Cut the silent tail (below 0.002 before the gain), with a 10 ms fade.
  let end = out.length;
  while (end > 1 && Math.abs(out[end - 1] as number) < 0.002 * GAIN) end--;
  const x = out.slice(0, end);
  const fade = Math.min(Math.round(0.01 * SR), x.length);
  for (let i = 0; i < fade; i++) x[x.length - 1 - i] = (x[x.length - 1 - i] as number) * (i / fade);
  return x;
}

const DRIVE = 3;
/** The ZzFX hurt's loudness over its first 120 ms, matched by the full punch. */
const GAIN = 0.698;

export function noise(secs: number, seed: number): Float32Array {
  // Mulberry32: the audition's noise, so the shipped punch is the one picked.
  let a = seed >>> 0;
  const b = new Float32Array(Math.round(secs * SR));
  for (let i = 0; i < b.length; i++) {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    b[i] = (((t ^ (t >>> 14)) >>> 0) / 4294967296) * 2 - 1;
  }
  return b;
}

/** RBJ biquad (direct form I). */
function biquad(x: Float32Array, f: number, q: number, b: (c: number, al: number) => [number, number, number]) {
  const w = (2 * Math.PI * f) / SR;
  const c = Math.cos(w);
  const al = Math.sin(w) / (2 * q);
  const [b0, b1, b2] = b(c, al);
  const a0 = 1 + al;
  const a1 = -2 * c;
  const a2 = 1 - al;
  const y = new Float32Array(x.length);
  let x1 = 0;
  let x2 = 0;
  let y1 = 0;
  let y2 = 0;
  for (let i = 0; i < x.length; i++) {
    const xi = x[i] as number;
    const v = (b0 * xi + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2) / a0;
    x2 = x1;
    x1 = xi;
    y2 = y1;
    y1 = v;
    y[i] = v;
  }
  return y;
}
export const lp = (x: Float32Array, f: number) => biquad(x, f, Math.SQRT1_2, (c) => [(1 - c) / 2, 1 - c, (1 - c) / 2]);
export const hp = (x: Float32Array, f: number) =>
  biquad(x, f, Math.SQRT1_2, (c) => [(1 + c) / 2, -(1 + c), (1 + c) / 2]);
export const bp = (x: Float32Array, f: number, q: number) => biquad(x, f, q, (_c, al) => [al, 0, -al]);

/** An attack ramp, then an exponential decay (tau s). */
export function env(x: Float32Array, attack: number, tau: number): Float32Array {
  const a = Math.max(1, Math.round(attack * SR));
  const k = 1 / (tau * SR);
  return x.map((v, i) => v * (i < a ? i / a : Math.exp(-(i - a) * k)));
}

/** A sine gliding from f0 to f1 Hz (exponentially, time constant tau s). */
export function sweep(secs: number, f0: number, f1: number, tau: number): Float32Array {
  const b = new Float32Array(Math.round(secs * SR));
  let ph = 0;
  for (let i = 0; i < b.length; i++) {
    ph += (2 * Math.PI * (f1 + (f0 - f1) * Math.exp(-i / (tau * SR)))) / SR;
    b[i] = Math.sin(ph);
  }
  return b;
}
